import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import OutlinedButton from '../../ui-library/OutlinedButton';
import { primitiveColors, semanticColors, semanticGradients, BORDER_RADIUS } from '@/theme/tokens';
import { feedbackSchema, type FeedbackData } from '../../schemas/feedbackSchema';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (feedback: string) => void;
  feedbackType: 'thumbs_up' | 'thumbs_down';
  conversationId?: string;
  messageId?: string;
  userId?: string;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  onClose,
  onSubmit,
  feedbackType,
  conversationId,
  messageId,
  userId,
}) => {
  const { t } = useTranslation('chat');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Validate the feedback data
      const feedbackData: FeedbackData = {
        user_id: userId || 'anonymous',
        conversation_id: conversationId,
        message_id: messageId || '',
        feedback_type: feedbackType,
        comment: feedback || undefined,
      };

      // Validate with Zod schema (throws on invalid data)
      feedbackSchema.parse(feedbackData);

      // If validation passes, submit the feedback
      onSubmit(feedback);
      setFeedback('');
      onClose();
    } catch (validationError) {
      if (validationError instanceof Error) {
        setError(validationError.message);
      } else {
        setError(t('feedbackModal.validationError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedback('');
    setError(null);
    setIsSubmitting(false);
    onClose();
  };

  const placeholderText =
    feedbackType === 'thumbs_up'
      ? t('feedbackModal.positivePlaceholder')
      : t('feedbackModal.negativePlaceholder');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: BORDER_RADIUS,
          px: '16px',
          py: '12px',
          backgroundColor: semanticColors.surface,
          maxWidth: '475px',
        },
      }}
    >
      <DialogTitle>
        <Typography
          component="div"
          fontWeight="bold"
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '1.25rem',
            color: semanticColors.textPrimary,
          }}
        >
          {t('feedbackModal.title')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontFamily: 'DM Sans, sans-serif' }}>
            {error}
          </Alert>
        )}
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 'bold',
            color: semanticColors.textPrimary,
          }}
        >
          {t('feedbackModal.detailsLabel')}
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={3}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={placeholderText}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: BORDER_RADIUS,
              '& fieldset': {
                borderColor: semanticColors.inputBorderFocus,
              },
              '&:hover fieldset': {
                borderColor: semanticColors.inputBorderFocus,
              },
              '&.Mui-focused fieldset': {
                borderColor: semanticColors.inputBorderFocus,
              },
            },
            '& .MuiInputBase-input': {
              color: semanticColors.textPrimary,
              fontFamily: 'DM Sans, sans-serif',
            },
            '& .MuiInputBase-input::placeholder': {
              color: semanticColors.textPrimary,
              opacity: 0.6,
            },
          }}
        />

        <Typography
          variant="caption"
          sx={{
            mt: 2,
            color: semanticColors.textPrimary,
            display: 'block',
            fontStyle: 'italic',
            fontSize: 14,
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          {t('feedbackModal.disclaimer')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, display: 'flex', gap: 2 }}>
        <OutlinedButton
          onClick={handleSubmit}
          disabled={isSubmitting}
          gradientOutline={true}
          styleOverrides={{
            root: {
              borderRadius: BORDER_RADIUS,
              background: semanticGradients.primary,
              padding: '1px',
              flex: 1,
              opacity: isSubmitting ? 0.6 : 1,
            },
            outlinedButton: {
              background: semanticColors.background,
              color: primitiveColors.rockSlate,
              border: '0.5px solid transparent',
              fontSize: 14,
              padding: '12px 16px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              width: '100%',
              height: '44px',
              fontFamily: 'DM Sans, sans-serif',
            },
          }}
        >
          {isSubmitting ? t('feedbackModal.submittingButton') : t('feedbackModal.submitButton')}
        </OutlinedButton>

        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: BORDER_RADIUS,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
            px: 2,
            borderColor: semanticColors.borderLight,
            color: semanticColors.textTertiary,
            flex: 1,
            height: '44px',
            fontFamily: 'DM Sans, sans-serif',
            '&:hover': {
              borderColor: semanticColors.borderMedium,
              backgroundColor: semanticColors.surfaceNeutral,
            },
          }}
        >
          {t('feedbackModal.cancelButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
